/**
 * Configuração centralizada de navegação mobile por módulo.
 *
 * Substitui ~450 linhas de constantes duplicadas em MobileBottomNav.tsx —
 * Os 9 módulos clone (Tribunais, Policiais, Jurídicas, CNU, Bancário,
 * Militares, Residência, Vestibulares, Colégios) são gerados a partir
 * do registry `MODULES` via `buildCloneNav`. Concursos / ENEM / OAB /
 * Videoaulas continuam com configs específicas porque divergem
 * (Flashcards, Plano TRI, Áreas Fracas, etc.).
 */
import {
  LayoutDashboard, FileQuestion, ClipboardList, TrendingUp, BookOpen, Timer,
  Star, RefreshCw, Trophy, Medal, History, Swords, Users, User, Settings,
  GraduationCap, Brain, AlertTriangle, BarChart3, Home, PlayCircle,
  Clock, PenLine, MapPin, Map, Search, Target, Flame, Award, type LucideIcon,
} from 'lucide-react';
import { MODULES, type ModuleSlug, type ModuleConfig } from '@/lib/modules/registry';

export interface MenuItem {
  label: string;
  icon: LucideIcon;
  path: string;
}

export interface MenuCategory {
  title: string;
  items: MenuItem[];
}

export interface ModuleColors {
  active: string;
  bg: string;
  border: string;
  sheetActive: string;
}

/** Chave usada para indexar o mapa de navegação (compatível com ModuleType). */
export type NavModuleKey =
  | 'concursos' | 'enem' | 'oab' | 'videoaulas' | 'vestibulares'
  | 'tribunais' | 'carreiras-policiais' | 'carreiras-juridicas'
  | 'cnu' | 'bancario' | 'militares' | 'residencia' | 'colegios';

// ============================================================================
// Helpers — geram nav dos módulos clone a partir do MODULES registry
// ============================================================================

function navItems(prefix: string): MenuItem[] {
  return [
    { label: 'Dashboard', icon: LayoutDashboard, path: prefix },
    { label: 'Questões', icon: FileQuestion, path: `${prefix}/questions` },
    { label: 'Simulados', icon: ClipboardList, path: `${prefix}/simulations` },
    { label: 'Desempenho', icon: TrendingUp, path: `${prefix}/analytics` },
  ];
}

function cloneMoreCategories(prefix: string, cfg: ModuleConfig): MenuCategory[] {
  const studyItems: MenuItem[] = [
    { label: 'Plano de Estudos', icon: Target, path: `${prefix}/study-plan` },
    ...(cfg.sidebar.showSmartReview
      ? [{ label: 'Meu Caderno', icon: Brain, path: `${prefix}/smart-review` } as MenuItem]
      : []),
    ...(cfg.sidebar.showWeakAreas
      ? [{ label: 'Áreas Fracas', icon: Flame, path: `${prefix}/weak-areas` } as MenuItem]
      : []),
    ...(cfg.sidebar.showPomodoro
      ? [{ label: 'Pomodoro', icon: Timer, path: `${prefix}/pomodoro` } as MenuItem]
      : []),
  ];

  const analysisItems: MenuItem[] = [
    ...(cfg.sidebar.showErrorPatterns
      ? [{ label: 'Padrões de Erro', icon: AlertTriangle, path: `${prefix}/error-patterns` } as MenuItem]
      : []),
    ...(cfg.sidebar.showAchievements
      ? [{ label: 'Conquistas', icon: Award, path: `${prefix}/achievements` } as MenuItem]
      : []),
  ];

  return [
    { title: 'Navegação', items: navItems(prefix) },
    ...(studyItems.length ? [{ title: 'Estudo', items: studyItems }] : []),
    {
      title: 'Análise',
      items: [
        { label: 'Ranking', icon: Medal, path: `${prefix}/ranking` },
        ...analysisItems,
      ],
    },
    {
      title: 'Conta',
      items: [
        { label: 'Configurações', icon: Settings, path: `${prefix}/settings` },
        { label: 'Perfil', icon: User, path: '/profile' },
      ],
    },
  ];
}

// ============================================================================
// Configs específicas (concursos / enem / oab / videoaulas)
// ============================================================================

const CONCURSOS_MAIN: MenuItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Questões', icon: FileQuestion, path: '/questions' },
  { label: 'Simulados', icon: ClipboardList, path: '/simulados' },
  { label: 'Desempenho', icon: TrendingUp, path: '/analytics' },
];

const CONCURSOS_MORE: MenuCategory[] = [
  { title: 'Navegação', items: CONCURSOS_MAIN },
  {
    title: 'Estudo',
    items: [
      { label: 'Plano de Estudos', icon: Target, path: '/concursos/study-plan' },
      { label: 'Flashcards', icon: BookOpen, path: '/flashcards' },
      { label: 'Pomodoro', icon: Timer, path: '/pomodoro' },
      { label: 'Favoritos', icon: Star, path: '/favorites' },
      { label: 'Meu Caderno', icon: RefreshCw, path: '/smart-review' },
    ],
  },
  {
    title: 'Social',
    items: [
      { label: 'Conquistas', icon: Trophy, path: '/achievements' },
      { label: 'Ranking', icon: Medal, path: '/ranking' },
      { label: 'Desafios', icon: Swords, path: '/challenges' },
      { label: 'Grupos', icon: Users, path: '/groups' },
    ],
  },
  {
    title: 'Progresso',
    items: [
      
      { label: 'Histórico', icon: History, path: '/history' },
      { label: 'Metas', icon: BarChart3, path: '/discipline-goals' },
    ],
  },
  {
    title: 'Conta',
    items: [
      { label: 'Perfil', icon: User, path: '/profile' },
      { label: 'Configurações', icon: Settings, path: '/settings/concursos' },
    ],
  },
];

const ENEM_MAIN: MenuItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/enem' },
  { label: 'Questões', icon: FileQuestion, path: '/enem/questions' },
  { label: 'Simulados', icon: ClipboardList, path: '/enem/simulations' },
  { label: 'Desempenho', icon: TrendingUp, path: '/enem/analytics' },
];

const ENEM_MORE: MenuCategory[] = [
  { title: 'Navegação', items: ENEM_MAIN },
  {
    title: 'Estudo',
    items: [
      { label: 'Pomodoro', icon: Timer, path: '/enem/pomodoro' },
      { label: 'Revisão Espaçada', icon: RefreshCw, path: '/enem/smart-review' },
      { label: 'Áreas Fracas', icon: AlertTriangle, path: '/enem/weak-areas' },
      { label: 'Padrões de Erro', icon: Brain, path: '/enem/errors' },
      { label: 'Simulado Completo', icon: Clock, path: '/enem/full-simulation' },
      { label: 'Redação', icon: PenLine, path: '/enem/redacao' },
    ],
  },
  {
    title: 'Planejamento',
    items: [
      { label: 'Plano de Estudos', icon: GraduationCap, path: '/enem/study-plan' },
      { label: 'Autoavaliação', icon: Brain, path: '/enem/autoavaliacao' },
    ],
  },
  {
    title: 'Análise ENEM',
    items: [
      { label: 'Panorama ENEM', icon: BarChart3, path: '/enem/estatisticas' },
      { label: 'Onde eu me encaixo', icon: MapPin, path: '/enem/onde-eu-estou' },
      { label: 'Mapa por estado', icon: Map, path: '/enem/mapa-ufs' },
      { label: 'Buscar escola/município', icon: Search, path: '/enem/buscar-escola' },
    ],
  },
  {
    title: 'Social',
    items: [
      { label: 'Conquistas', icon: Trophy, path: '/enem/achievements' },
      { label: 'Ranking', icon: Medal, path: '/enem/ranking' },
    ],
  },
  {
    title: 'Conta',
    items: [
      { label: 'Configurações', icon: Settings, path: '/enem/settings' },
      { label: 'Perfil', icon: User, path: '/profile' },
    ],
  },
];

const OAB_MAIN: MenuItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/oab' },
  { label: 'Questões', icon: FileQuestion, path: '/oab/questions' },
  { label: 'Simulados', icon: ClipboardList, path: '/oab/simulations' },
  { label: 'Desempenho', icon: TrendingUp, path: '/oab/analytics' },
];

const OAB_MORE: MenuCategory[] = [
  { title: 'Navegação', items: OAB_MAIN },
  {
    title: 'Estudo',
    items: [
      { label: 'Plano de Estudos', icon: Target, path: '/oab/study-plan' },
      { label: 'Pomodoro', icon: Timer, path: '/oab/pomodoro' },
    ],
  },
  { title: 'Social', items: [{ label: 'Ranking', icon: Medal, path: '/oab/ranking' }] },
  {
    title: 'Conta',
    items: [
      { label: 'Configurações', icon: Settings, path: '/oab/settings' },
      { label: 'Perfil', icon: User, path: '/profile' },
    ],
  },
];

const VIDEOAULAS_MAIN: MenuItem[] = [
  { label: 'Home', icon: Home, path: '/videoaulas' },
  { label: 'Catálogo', icon: PlayCircle, path: '/videoaulas/catalogo' },
  { label: 'Meus Cursos', icon: BookOpen, path: '/videoaulas/meus-cursos' },
  { label: 'Histórico', icon: History, path: '/videoaulas/historico' },
];

const VIDEOAULAS_MORE: MenuCategory[] = [
  { title: 'Navegação', items: VIDEOAULAS_MAIN },
  { title: 'Progresso', items: [{ label: 'Estatísticas', icon: BarChart3, path: '/videoaulas/estatisticas' }] },
  { title: 'Conta', items: [{ label: 'Perfil', icon: User, path: '/profile' }] },
];

// ============================================================================
// Cores por módulo
// ============================================================================

export const MODULE_COLORS: Record<NavModuleKey, ModuleColors> = {
  concursos: {
    active: 'text-primary',
    bg: 'bg-background',
    border: 'border-t',
    sheetActive: 'bg-primary/10 text-primary border-l-2 border-primary',
  },
  enem: {
    active: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-950 dark:to-purple-950',
    border: 'border-t-2 border-blue-500/60',
    sheetActive: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-l-2 border-blue-500',
  },
  oab: {
    active: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-950 dark:to-orange-950',
    border: 'border-t-2 border-amber-500/60',
    sheetActive: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-l-2 border-amber-500',
  },
  vestibulares: {
    active: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-gradient-to-r from-indigo-100 to-violet-100 dark:from-indigo-950 dark:to-violet-950',
    border: 'border-t-2 border-indigo-500/60',
    sheetActive: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-l-2 border-indigo-500',
  },
  videoaulas: {
    active: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-950 dark:to-pink-950',
    border: 'border-t-2 border-purple-500/60',
    sheetActive: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-l-2 border-purple-500',
  },
  tribunais: {
    active: 'text-indigo-700 dark:text-indigo-300',
    bg: 'bg-gradient-to-r from-indigo-100 to-blue-100 dark:from-indigo-950 dark:to-blue-950',
    border: 'border-t-2 border-indigo-600/60',
    sheetActive: 'bg-indigo-600/10 text-indigo-700 dark:text-indigo-300 border-l-2 border-indigo-600',
  },
  'carreiras-policiais': {
    active: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-gradient-to-r from-blue-100 to-sky-100 dark:from-blue-950 dark:to-sky-950',
    border: 'border-t-2 border-blue-700/60',
    sheetActive: 'bg-blue-700/10 text-blue-700 dark:text-blue-300 border-l-2 border-blue-700',
  },
  'carreiras-juridicas': {
    active: 'text-[#7C1D2E] dark:text-rose-300',
    bg: 'bg-gradient-to-r from-rose-100 to-red-100 dark:from-rose-950 dark:to-red-950',
    border: 'border-t-2 border-[#7C1D2E]/60',
    sheetActive: 'bg-[#7C1D2E]/10 text-[#7C1D2E] dark:text-rose-300 border-l-2 border-[#7C1D2E]',
  },
  cnu: {
    active: 'text-[#00853F] dark:text-emerald-300',
    bg: 'bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-950 dark:to-green-950',
    border: 'border-t-2 border-[#00853F]/60',
    sheetActive: 'bg-[#00853F]/10 text-[#00853F] dark:text-emerald-300 border-l-2 border-[#00853F]',
  },
  bancario: {
    active: 'text-[#003D7C] dark:text-yellow-300',
    bg: 'bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-950/60 dark:to-blue-950',
    border: 'border-t-2 border-[#FFD500]/70',
    sheetActive: 'bg-[#FFD500]/15 text-[#003D7C] dark:text-yellow-300 border-l-2 border-[#FFD500]',
  },
  militares: {
    active: 'text-[#4B5320] dark:text-[#C9B037]',
    bg: 'bg-gradient-to-r from-[#4B5320]/10 to-[#C9B037]/10 dark:from-[#4B5320]/40 dark:to-[#C9B037]/20',
    border: 'border-t-2 border-[#4B5320]/70',
    sheetActive: 'bg-[#4B5320]/15 text-[#4B5320] dark:text-[#C9B037] border-l-2 border-[#4B5320]',
  },
  residencia: {
    active: 'text-[#0E7C5A] dark:text-emerald-300',
    bg: 'bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-950/60 dark:to-green-950/60',
    border: 'border-t-2 border-[#0E7C5A]/70',
    sheetActive: 'bg-[#0E7C5A]/15 text-[#0E7C5A] dark:text-emerald-300 border-l-2 border-[#0E7C5A]',
  },
  colegios: {
    active: 'text-[#3D5240] dark:text-emerald-300',
    bg: 'bg-gradient-to-r from-emerald-100 to-green-100 dark:from-[#3D5240]/40 dark:to-emerald-950/60',
    border: 'border-t-2 border-[#3D5240]/70',
    sheetActive: 'bg-[#3D5240]/15 text-[#3D5240] dark:text-emerald-300 border-l-2 border-[#3D5240]',
  },
};

// ============================================================================
// Mapa final: chaves de módulo → { mainItems, moreCategories }
// ============================================================================

interface ModuleNav {
  mainItems: MenuItem[];
  moreCategories: MenuCategory[];
}

/** Mapeia o slug do registry para a chave de navegação (ModuleType). */
const SLUG_TO_NAV_KEY: Record<ModuleSlug, NavModuleKey> = {
  tribunais: 'tribunais',
  policiais: 'carreiras-policiais',
  juridicas: 'carreiras-juridicas',
  cnu: 'cnu',
  bancario: 'bancario',
  militares: 'militares',
  residencia: 'residencia',
  colegios: 'colegios',
  vestibulares: 'vestibulares',
};

const CLONE_NAV: Partial<Record<NavModuleKey, ModuleNav>> = Object.fromEntries(
  Object.values(MODULES).map((cfg) => {
    const navKey = SLUG_TO_NAV_KEY[cfg.slug];
    const main = navItems(cfg.routePrefix);
    return [navKey, {
      mainItems: main,
      moreCategories: cloneMoreCategories(cfg.routePrefix, cfg),
    }] as const;
  }),
) as Partial<Record<NavModuleKey, ModuleNav>>;

const MODULE_NAV: Record<NavModuleKey, ModuleNav> = {
  concursos: { mainItems: CONCURSOS_MAIN, moreCategories: CONCURSOS_MORE },
  enem: { mainItems: ENEM_MAIN, moreCategories: ENEM_MORE },
  oab: { mainItems: OAB_MAIN, moreCategories: OAB_MORE },
  videoaulas: { mainItems: VIDEOAULAS_MAIN, moreCategories: VIDEOAULAS_MORE },
  // Os módulos clone (gerados via registry) preenchem o resto:
  ...(CLONE_NAV as Record<NavModuleKey, ModuleNav>),
};

/** Retorna `{ mainItems, moreCategories, colors }` para o módulo atual. */
export function getModuleNav(moduleKey: string) {
  const key = (moduleKey as NavModuleKey);
  const nav = MODULE_NAV[key] ?? MODULE_NAV.concursos;
  const colors = MODULE_COLORS[key] ?? MODULE_COLORS.concursos;
  return { ...nav, colors };
}

/**
 * Detecta o módulo a partir do pathname atual.
 * Retorna null se a rota é "neutra" (perfil, dashboard global, etc.) e o
 * caller deve consultar `localStorage.last_active_module` como fallback.
 */
export function detectModuleFromPath(pathname: string): NavModuleKey | null {
  if (pathname.startsWith('/videoaulas')) return 'videoaulas';
  if (pathname.startsWith('/vestibulares')) return 'vestibulares';
  if (pathname.startsWith('/carreiras-policiais')) return 'carreiras-policiais';
  if (pathname.startsWith('/carreiras-juridicas')) return 'carreiras-juridicas';
  if (pathname.startsWith('/cnu')) return 'cnu';
  if (pathname.startsWith('/bancario')) return 'bancario';
  if (pathname.startsWith('/militares')) return 'militares';
  if (pathname.startsWith('/residencia')) return 'residencia';
  if (pathname.startsWith('/tribunais')) return 'tribunais';
  if (pathname.startsWith('/colegios')) return 'colegios';
  if (pathname.startsWith('/oab')) return 'oab';
  if (pathname.startsWith('/enem')) return 'enem';
  return null;
}
